import { ImportFailure, ImportPayloadRow, ImportProgress, ImportSummary } from "./types";

/** Filas por invocacion. La lambda tolera hasta 200; 100 deja margen y da progreso fino. */
export const IMPORT_BATCH_SIZE = 100;

export type BatchOutcome = {
    createdCount: number;
    updatedCount: number;
    failures: ImportFailure[];
};

/**
 * Envia las filas a la lambda de importacion en lotes secuenciales.
 *
 * Va lote por lote (en vez de en paralelo) para poder reportar progreso real y para no
 * saturar la capacidad de escritura de DynamoDB. Los indices de las filas que fallan se
 * corrigen al offset del lote para que apunten a la fila del archivo.
 */
export const runImportInBatches = async <TRow extends { action?: string } = ImportPayloadRow>(
    rows: TRow[],
    invokeBatch: (batch: TRow[]) => Promise<BatchOutcome>,
    onProgress?: (progress: ImportProgress) => void,
    // La carga de comprobantes usa un lote mas chico: cada documento son varias escrituras
    // (cabecera, lineas y pagos), no una sola fila.
    batchSize: number = IMPORT_BATCH_SIZE
): Promise<ImportSummary> => {
    const summary: ImportSummary = { createdCount: 0, updatedCount: 0, failures: [] };
    const writable = rows.filter((row) => row.action !== "skip");

    onProgress?.({ processed: 0, total: writable.length });

    for (let offset = 0; offset < writable.length; offset += batchSize) {
        const batch = writable.slice(offset, offset + batchSize);
        const outcome = await invokeBatch(batch);

        summary.createdCount += outcome.createdCount;
        summary.updatedCount += outcome.updatedCount;
        summary.failures.push(
            ...outcome.failures.map((failure) => ({ ...failure, index: failure.index + offset }))
        );

        onProgress?.({ processed: Math.min(offset + batch.length, writable.length), total: writable.length });
    }

    return summary;
};

export const parseFailures = (raw?: string | null): ImportFailure[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};
